import { Repeat } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Badge } from '@/components/badge';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { usePortalSubscriptions } from '@/lib/portal-subscriptions';
import { intervalLabel } from '@/lib/subscriptions';
import { dollars, formatDate, formatStatus } from '@/lib/format';
import type { PortalSubscriptionData } from '@/types/api';

export function PortalSubscriptions() {
  const { data, isLoading } = usePortalSubscriptions();

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Repeat}
        title="Subscriptions"
        subtitle="Your ongoing subscriptions and retainers with the Mehrutherm team."
      />

      {isLoading ? (
        <SkeletonList rows={3} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title="No active subscriptions"
          description="Your recurring subscriptions and retainers with Mehrutherm will appear here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.map((sub) => (
            <ServiceCard key={sub.id} sub={sub} />
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceCard({ sub }: { sub: PortalSubscriptionData }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-medium">{sub.description ?? 'Subscription'}</div>
            <div className="mt-0.5 text-xs text-[var(--color-foreground-muted)]">
              {intervalLabel(sub.interval, sub.interval_count)}
            </div>
          </div>
          <Badge tone={sub.status === 'active' || sub.status === 'trialing' ? 'success' : 'neutral'}>
            {formatStatus(sub.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-2 text-sm">
        <Row label="Amount" value={`${dollars(sub.amount_cents)} / ${sub.interval}`} />
        {sub.current_period_end ? (
          <Row
            label={sub.cancel_at_period_end ? 'Ends' : 'Renews'}
            value={formatDate(sub.current_period_end)}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[var(--color-foreground-muted)]">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}
